import React from 'react';
import {
  Alert,
  Frame,
  Icon,
  Input,
  SageClassnames,
  SageTokens,
  Tooltip,
  useSageBreakpoint,
} from '@kajabi/sage-react';
import PropTypes from 'prop-types';
import { CardElement } from '@stripe/react-stripe-js';

import { ELEMENT_OPTIONS } from 'common/components/StripeForm';
import { useStripeForm } from 'common/components/StripeForm/context';

import { useCheckout } from '../../context';
import BusinessVatInput from '../../../growth/onboarding-framework/components/component-slides/business-address-slide/business-vat-input';

export const InputFields = ({ minified }) => {
  const {
    errors,
    formParams,
    handleFormChange,
    planInfo: { cardless, further_email_validation_required: furtherEmailValidationRequired },
    social,
    isTwoStepsSignupFirstStep,
    inputPasswordRef,
    successfulExpressCheckout,
  } = useCheckout();
  const { cardErrors, handleCardChange } = useStripeForm();
  const breakpoints = useSageBreakpoint();

  const renderPaymentDetails = () => {
    if (isTwoStepsSignupFirstStep() || successfulExpressCheckout || cardless) {
      return null;
    }

    return (
      <Frame
        className={cardErrors.card ? 'sage-form-field--error' : ''}
        direction={Frame.DIRECTIONS.VERTICAL}
        gap={Frame.GAPS.NONE}
        width={Frame.WIDTHS.FILL}
      >
        <label className="sage-input__label sage-frame--width-fill">Payment information</label>
        <CardElement onChange={handleCardChange} options={ELEMENT_OPTIONS} />
        <div className="sage-input__message">{cardErrors.card}</div>
      </Frame>
    );
  };

  const renderPasswordDetails = () => {
    if (isTwoStepsSignupFirstStep()) {
      return null;
    }

    return (
      social || (
        <Frame direction={Frame.DIRECTIONS[breakpoints.sm ? 'VERTICAL' : 'HORIZONTAL']}>
          <Input
            id="password"
            ref={inputPasswordRef}
            className="sage-frame--width-fill"
            hasError={!!errors.password?.length}
            label="Create password"
            message={errors.password?.join(', ')}
            onChange={handleFormChange}
            type="password"
            value={formParams.password || ''}
          />
          <Input
            id="password_confirmation"
            className="sage-frame--width-fill"
            hasError={!!errors.password_confirmation?.length}
            label="Confirm password"
            message={errors.password_confirmation?.join(', ')}
            onChange={handleFormChange}
            type="password"
            value={formParams.password_confirmation || ''}
          />
        </Frame>
      )
    );
  };

  return (
    <Frame
      borderRadius={Frame.BORDER_RADII[minified ? 'MD' : 'NONE']}
      padding={Frame.PADDINGS[minified ? 'MD' : 'NONE']}
      background={SageTokens.COLOR_SLIDERS[minified ? 'GREY_200' : 'NONE']}
    >
      {successfulExpressCheckout && (
        <h6 className={SageClassnames.TYPE.HEADING_6}>Finish completing your account</h6>
      )}
      <Frame direction={Frame.DIRECTIONS[breakpoints.sm ? 'VERTICAL' : 'HORIZONTAL']}>
        <Input
          id="first_name"
          className="sage-frame--width-fill"
          hasError={!!errors.first_name?.length}
          label="First name"
          message={errors.first_name?.join(', ')}
          onChange={handleFormChange}
          value={formParams.first_name || ''}
        />
        <Input
          id="last_name"
          className="sage-frame--width-fill"
          hasError={!!errors.last_name?.length}
          label="Last name"
          message={errors.last_name?.join(', ')}
          onChange={handleFormChange}
          value={formParams.last_name || ''}
        />
      </Frame>
      <Frame gap={Frame.GAPS.NONE}>
        <Frame align={Frame.ALIGNMENTS.START_SPREAD} direction={Frame.DIRECTIONS.HORIZONTAL}>
          <label className="sage-input__label" htmlFor="email">
            Email address
          </label>
          {social && !furtherEmailValidationRequired && (
            <Tooltip
              content={
                <div style={{ textAlign: 'left' }}>
                  <p
                    className={`${SageClassnames.SPACERS.SM_BOTTOM} ${SageClassnames.TYPE.BODY_SMALL_MED}`}
                  >
                    Pre-populated email address.
                  </p>
                  <p className={SageClassnames.TYPE.BODY_SMALL}>
                    This email has been passed from your social authentication. You can always
                    update anytime after account creation
                  </p>
                </div>
              }
            >
              <Icon icon={SageTokens.ICONS.INFO_CIRCLE} />
            </Tooltip>
          )}
        </Frame>
        <Input
          id="email"
          className="sage-frame--width-fill"
          disabled={social && !furtherEmailValidationRequired}
          hasError={!!errors.email?.length}
          label="Email address"
          message={errors.email?.join(', ')}
          onChange={handleFormChange}
          value={formParams.email || ''}
        />
      </Frame>
      {renderPasswordDetails()}
      {renderPaymentDetails()}
      {errors.base && (
        <Alert
          color={Alert.COLORS.DANGER}
          description={errors.base.join(', ')}
          style={{ width: '100%' }}
          title="Error creating subscription"
        />
      )}
      <BusinessVatInput value={formParams.vat_number || ''} />
    </Frame>
  );
};

InputFields.defaultProps = {
  minified: false,
};

InputFields.propTypes = {
  minified: PropTypes.bool,
};
